import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import * as turf from '@turf/simplify';
import { FeatureCollection } from 'geojson';
import debounce from 'lodash/debounce';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import * as wkt from 'wkt';
import * as wkx from 'wkx';

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { GoogleDrawingManager } from '@lib/geo/google/drawing';
import { GoogleMap, GoogleMapStyle } from '@lib/geo/google/map';
import { MixpanelService } from '@lib/mixpanel';
import { BASE_COLORS, COLORS } from '@lib/ui/theme';

import { SnLprRead } from '../api/models/lpr-read';
import { FsLprCameraAdvancedMarker, FsLprReadAdvancedMarker } from '../components/map';
import { EMPTY_POLYGON_WKB, QueriesSearchParams } from '../utils';
import { LprIntegrationsService } from './lpr-integrations.service';

type DrawingShapes =
  | google.maps.Polygon
  | google.maps.Circle
  | google.maps.Polyline
  | google.maps.Rectangle;

@Injectable({ providedIn: 'root' })
export class PlatesService implements OnDestroy {
  public map: GoogleMap;
  public drawingManager: GoogleDrawingManager;
  public activeStrokeColor: string = BASE_COLORS['accent-400'];
  public activeFillColor: string = BASE_COLORS['accent-400'];
  public bBoxFillColor: string = COLORS['red-900'];
  public strokeWeight: number = 6;
  public strokeOpacity: number = 1;
  public fillOpacity: number = 0.2;
  public isDragging: boolean = false;
  public lprMarkers: FsLprCameraAdvancedMarker[] = [];
  public resultMarkers: SnLprRead[] = [];
  public activeResultMarkers: FsLprReadAdvancedMarker[] = [];
  public showBbox: boolean = false;

  public lprCameras = [];
  public drawnAreaCameras = [];
  public activeMakes: string[] = [];
  public makesArray: string[] = [];
  public queriesSearchParams: QueriesSearchParams = {};
  public deckGLoverlay: DeckOverlay;
  public hasMapViewSearched: boolean = false;
  public mapInteracted: boolean = true; 
  public hasBoundarySearched: boolean = false;

  private _drawingMode: string;
  private _activeShape: DrawingShapes | null = null;
  private _activeShapeBbox: google.maps.Polygon | null = null;
  private _penMoveCallListener: google.maps.MapsEventListener;
  private _freeDrawPolyline: any;

  private readonly _mapLoaded$ = new Subject<any>();
  private readonly _shapeComplete$: Subject<DrawingShapes> = new Subject();
  private readonly _plate$ = new Subject<SnLprRead>();
  private readonly _plates$ = new Subject<SnLprRead[]>();
  private readonly _fields$ = new Subject<any>();
  private readonly _clearMarkers$ = new Subject<void>();
  private readonly _reload$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();
  private readonly _patchQueriesForm$ = new Subject<any>();
  private readonly _markerClicked$ = new Subject<any>();
  private readonly _markerHovered$ = new Subject<any>();
  public projectionOverlay: google.maps.OverlayView;
  private _shapeClickListener: google.maps.MapsEventListener;
  private _shapeMoveListener: google.maps.MapsEventListener;
  private _allShapeListeners: google.maps.MapsEventListener[] = [];

  private DrawingManagerClass: typeof GoogleDrawingManager;

  get cadplate$(): Observable<SnLprRead> {
    return this._plate$.asObservable();
  }

  get plates$(): Observable<SnLprRead[]> {
    return this._plates$.asObservable();
  }

  get fields$(): Observable<SnLprRead[]> {
    return this._fields$.asObservable();
  }

  get clearMarkers$(): Observable<void> {
    return this._clearMarkers$.asObservable();
  }

  get mapLoaded$(): Observable<any> {
    return this._mapLoaded$.asObservable();
  }

  get reload$(): Observable<any> {
    return this._reload$.asObservable();
  }

  get markerClicked$(): Observable<any> {
    return this._markerClicked$.asObservable();
  }

  get markerHovered$(): Observable<any> {
    return this._markerHovered$.asObservable();
  }

  get lprIntegrationTypes(): any[] {
    return this.lprIntegrationsService.integrations;
  }

  get shapeComplete$(): Observable<DrawingShapes> {
    return this._shapeComplete$.asObservable();
  }

  get patchQueriesForm$(): Observable<any> {
    return this._patchQueriesForm$.asObservable();
  }

  constructor(
    public readonly lprIntegrationsService: LprIntegrationsService,
    private readonly _httpClient: HttpClient,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _toastrService: ToastrService,
    private readonly _mixpanelService: MixpanelService,
    private readonly _router: Router,
    @Optional() @Inject('DrawingManagerClass') DrawingManagerClass?: typeof GoogleDrawingManager,
  ) {
    this.makesArray = this.lprIntegrationTypes;
    this._initProjectionOverlay();
    this.DrawingManagerClass = DrawingManagerClass || GoogleDrawingManager;
  }

  get drawingMode(): string {
    return this._drawingMode;
  }

  get activeShape(): any {
    return this._activeShape;
  }

  public toggleShapes(setVisible: boolean): void {
    if (this._activeShape) {
      this._activeShape.setVisible(setVisible);
    }
    if (this._activeShapeBbox && this.showBbox) {
      this._activeShapeBbox.setVisible(setVisible);
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._removeShapeListeners();
  }

  public getPlates(params: ISnListQueryParameters): Observable<ISnListResponse<SnLprRead>> {
    return this._httpClient.get<ISnListResponse<SnLprRead>>('/api/lpr/reads/', { params });
  }

  public getPlateById(id: string): Observable<SnLprRead> {
    return this._httpClient
      .get(`/api/lpr/reads/${id}`)
      .pipe(map((response) => new SnLprRead(response)));
  }

  public onPlateClick(plate: SnLprRead, fields?: any): void {
    this._plate$.next(null);
    this._plate$.next(plate);
    if (fields) {
      this._fields$.next(fields);
    }
  }

  public setPlates(plates: any[]): void {
    this._plates$.next(plates);
  }

  public clearMarkers(): void {
    this._clearMarkers$.next();
  }

  public clearShape(reload: boolean = true): void {
    this._mixpanelService.trackMetric(MxEventName.MapInteractionsLprSearchAf, {
      [MxPropertyName.MapAction]: 'Remove Boundary',
    });
    if (this._activeShape) {
      this._activeShape.setMap(null);
      this._activeShape = null;
    }
    if (this._activeShapeBbox) {
      this._activeShapeBbox.setMap(null);
      this._activeShapeBbox = null;
      this._clearUrlQueryParams();
    }
    this._resetParams();
    this.hasBoundarySearched = false; // Reset when shape is cleared
    if (reload) {
      this._reload$.next();
    }
    this._removeShapeListeners();
    this._shapeComplete$.next();
  }

  public resetActiveMakes(): void {
    this.makesArray.forEach((make) => {
      this.activeMakes.push(make);
    });
  }

  public onMapInit(googleMap: GoogleMap): void {
    this.map = googleMap;
    this.map.setNewMapID(GoogleMapStyle.Dark);
    this._mapLoaded$.next(this.map);
  }

  /**
   * Checks URL query parameters for point and distance parameters to create a bounding box for search.
   *
   * This can happen when a user draws a shape on the main dashboard map and kicks off an LPR
   * search from there.
   *
   * The circle is converted to a polygon -> bounding box -> wkt string for API compatibility.
   * If a polygon is present, it converted to a bounding box -> wkt string.
   */
  public initShapeFromQueryParams(): void {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    const polygon = queryParams.polygon;
    const point = queryParams.point;
    const distance = queryParams.dist;
    const cameraDetails = queryParams.details__camera_name__in;
    if (polygon) {
      const parsedGeojson = wkt.parse(polygon);
      const points = parsedGeojson.coordinates[0].map(([lon, lat]) => {
        return new google.maps.LatLng(lat, lon);
      });
      this._activeShape = new google.maps.Polygon({
        paths: [points],
        map: this.map.mapInstance,
        editable: true,
        draggable: true,
        strokeColor: this.activeStrokeColor,
        fillColor: this.activeFillColor,
        fillOpacity: this.fillOpacity,
        strokeOpacity: this.strokeOpacity,
        strokeWeight: this.strokeWeight,
      });
    } else if (point && distance) {
      const radius = parseFloat(distance);
      const lonLat = point.split(',');
      const center = new google.maps.LatLng(parseFloat(lonLat[1]), parseFloat(lonLat[0]));
      this._activeShape = new google.maps.Circle({
        center,
        radius,
        map: this.map.mapInstance,
        editable: true,
        draggable: true,
        strokeColor: this.activeStrokeColor,
        fillColor: this.activeFillColor,
        fillOpacity: this.fillOpacity,
        strokeOpacity: this.strokeOpacity,
        strokeWeight: this.strokeWeight,
      });
    }
    if (cameraDetails) {
      this._reload$.next();
    }
    if (this._activeShape) {
      this._attachListeners(this._activeShape);
    }
  }

  public addShape(type: string): void {
    if (this._activeShape) {
      this._activeShape.setMap(null);
      this._activeShape = null;
      this._removeShapeListeners();
    }
    if (this._activeShapeBbox) {
      this._activeShapeBbox.setMap(null);
      this._activeShapeBbox = null;
      this._clearUrlQueryParams();
    }
    if (type === 'rectangle') {
      this.setDrawingMode(google.maps.drawing.OverlayType.RECTANGLE);
      this._mixpanelService.trackMetric(MxEventName.MapInteractionsLprSearchAf, {
        [MxPropertyName.MapAction]: 'Draw Rectangle',
      });
    }
  }

  public inShape(result: SnLprRead): boolean {
    if (!this._activeShape) {
      return true;
    }
    return this._activeShape instanceof google.maps.Polygon
      ? google.maps.geometry.poly.containsLocation(result.googleLatLng, this._activeShape)
      : this._activeShape instanceof google.maps.Circle
      ? this._pointInCircle(
          result.googleLatLng,
          this._activeShape.getRadius(),
          this._activeShape.getCenter(),
        )
      : false;
  }

  public setDrawingMode(overlayType?: google.maps.drawing.OverlayType): void {
    this.resetMapOptions();
    if (this.drawingMode === 'pen') {
      google.maps.event.clearListeners(this.map.mapInstance, 'mousemove');
      google.maps.event.clearListeners(this.map.mapInstance, 'mouseup');
      google.maps.event.clearListeners(this.map.mapInstance, 'mousedown');
      this.map.mapInstance.setOptions({
        draggable: true,
        scrollwheel: true,
        disableDoubleClickZoom: true,
      });
      if (this._freeDrawPolyline) {
        this._freeDrawPolyline.setMap(null);
        this._freeDrawPolyline = null;
      }
    }
    this._drawingMode = overlayType;
    this.drawingManager.setDrawingMode(overlayType);
  }

  public getDrawingManagerMode(): google.maps.drawing.OverlayType {
    if (!this.drawingManager) {
      return;
    }
    return this.drawingManager?.getDrawingMode();
  }

  public resetMapOptions(): void {
    this.map.mapInstance.setOptions({
      draggable: true,
      scrollwheel: true,
      disableDoubleClickZoom: false,
      draggableCursor: '',
    });
  }

  public initDrawingManager(): void {
    this.drawingManager = new this.DrawingManagerClass(this.map, {
      polygonOptions: {
        editable: true,
        draggable: true,
        strokeColor: this.activeStrokeColor,
        fillColor: this.activeFillColor,
        fillOpacity: this.fillOpacity,
        strokeOpacity: this.strokeOpacity,
        strokeWeight: this.strokeWeight,
      },
      circleOptions: {
        editable: true,
        draggable: true,
        strokeColor: this.activeStrokeColor,
        fillColor: this.activeFillColor,
        fillOpacity: this.fillOpacity,
        strokeOpacity: this.strokeOpacity,
        strokeWeight: this.strokeWeight,
      },
      rectangleOptions: {
        editable: true,
        draggable: true,
        strokeColor: this.activeStrokeColor,
        fillColor: this.activeFillColor,
        fillOpacity: this.fillOpacity,
        strokeOpacity: this.strokeOpacity,
        strokeWeight: this.strokeWeight,
      },
    });

    this.drawingManager
      .on('polygoncomplete')
      .pipe(takeUntil(this._destroy$))
      .subscribe((polygon: google.maps.Polygon) => {
        const points = polygon
          .getPath()
          .getArray()
          .map((latLng) => [latLng.lat(), latLng.lng()]);
        if (points.length < 3) {
          this._toastrService.error('Not enough points to place boundary', 'Invalid Polygon');
          polygon.setMap(null);
          this.setDrawingMode();
          return;
        }
        this._shapeComplete$.next(polygon);
        this._activeShape = polygon;
        this._attachListeners(this._activeShape);
        this.setDrawingMode();
      });
    this.drawingManager
      .on('circlecomplete')
      .pipe(takeUntil(this._destroy$))
      .subscribe((circle: google.maps.Circle) => {
        this._shapeComplete$.next(circle);
        this._activeShape = circle;
        this._attachListeners(this._activeShape);
        this.setDrawingMode();
      });
    this.drawingManager
      .on('rectanglecomplete')
      .pipe(takeUntil(this._destroy$))
      .subscribe((rectangle: google.maps.Rectangle) => {
        this._shapeComplete$.next(rectangle);
        this._activeShape = rectangle;
        this._attachListeners(this._activeShape);
        this.setDrawingMode();
      });
  }

  /**
   * Calculates and draws the bounding box for the shape drawn on the map and updates the query
   * params.
   *
   * For ALPR, providers search by the bounding box that contains the drawn shape. Some
   * providers accept only two coordinates, while others only accept a polygon.
   *  - `fs_bbox` contains the two coordinates to form the opposite corners of the rectangle.
   *  - `bboxPolygon` contains four points for all verticies of the rectangle.
   *
   * @param shape a polygon, circle, or rectangle
   */
  public updateShape(shape: DrawingShapes): void {
    // Clear the Bbox overlay whenever a shape is edited; ex. dragged or expanded
    if (this._activeShapeBbox) {
      this._activeShapeBbox.setMap(null);
      this._activeShapeBbox = null;
    }
    this._resetParams();
    this.hasBoundarySearched = false; // Reset when new shape is drawn

    const dataLayer = new google.maps.Data();
    if (!(shape instanceof google.maps.Polyline)) {
      const { fs_bbox, bboxPolygon, activeShapeBbox } = this._getBboxFromShape(shape);
      this.queriesSearchParams.fs_bbox = fs_bbox;
      this._activeShapeBbox = activeShapeBbox;

      // No need to show the activeShapeBbox for rectangle because the shape is itself the bounding box
      if (!(shape instanceof google.maps.Rectangle)) {
        this._activeShapeBbox.setMap(this.drawingManager.mapInstance);
      }
      dataLayer.add(bboxPolygon);
    }

    if (shape instanceof google.maps.Polygon) {
      dataLayer.add(
        new google.maps.Data.Feature({
          geometry: new google.maps.Data.Polygon([
            shape
              .getPath()
              .getArray()
              .map((latLng) => new google.maps.LatLng(latLng.lat(), latLng.lng())),
          ]),
          properties: {
            type: 'polygon',
          },
        }),
      );
    } else if (shape instanceof google.maps.Circle) {
      const center = shape.getCenter();
      const coordinates = this.circlePath(center, shape.getRadius(), 360).map(
        (coor) => new google.maps.LatLng(coor[0], coor[1]),
      );
      dataLayer.add(
        new google.maps.Data.Feature({
          geometry: new google.maps.Data.Polygon([coordinates]),
          properties: {
            center: { lat: center.lat(), lng: center.lng() },
            radius: shape.getRadius(),
            type: 'circle',
          },
        }),
      );
    }

    dataLayer.toGeoJson((featureCollection: FeatureCollection) => {
      const bboxPolygonFeature = featureCollection.features[0];
      if (!(shape instanceof google.maps.Polyline)) {
        // Store both WKT and WKB formats
        const wktString = wkt.stringify(bboxPolygonFeature);

        // For axon_connect endpoint, use WKB format
        this.queriesSearchParams.bboxPolygonWKB = this._wktToWkbHex(wktString);

        // For all other endpoints, keep using WKT format
        this.queriesSearchParams.bboxPolygon = wktString;
      }

      // Render bbox on the bottom, active shape next, and markers on top of everything
      this._activeShapeBbox.setOptions({ zIndex: 0 });
      this._activeShape.setOptions({ zIndex: 1 });

      // Ensure that any shape completion triggers proper marker visibility
      // This will help prevent markers from disappearing after drawing operations
      this._shapeComplete$.next(shape);
    });
  }

  /**
   * Converts a circle into an n-sided polygon.
   *
   * @param center the LatLng coordinates for the circle's center
   * @param radius the radius of the circle
   * @param points how many sides the polygon will be
   * @returns an array of [lat, lng] in the path of a circle
   */
  public circlePath(center: google.maps.LatLng, radius: number, points: number): any[] {
    const paths = [];
    const factor = 360 / points;
    let distance = 0;
    for (let i = 0; i < points; i += 1, distance += factor) {
      const lat = google.maps.geometry.spherical.computeOffset(center, radius, distance).lat();
      const lng = google.maps.geometry.spherical.computeOffset(center, radius, distance).lng();
      paths.push([lat, lng]);
    }
    return paths;
  }

  public createResultsMarker(): void {
    this.resultMarkers.forEach((result: SnLprRead) => {
      const marker = new FsLprReadAdvancedMarker(result);
      this.activeResultMarkers.push(marker);
      marker.addTo(this.map);
      marker.getMarker().addListener('click', () => {
        this.onPlateClick(result);
      });
    });
  }

  public removeResultsMarker(): void {
    this.resultMarkers = [];
    if (this.activeResultMarkers?.length) {
      this.activeResultMarkers.forEach((marker: FsLprReadAdvancedMarker) => {
        marker.remove();
      });
    }
    this.activeResultMarkers = [];
  }

  public patchQueriesForm(): void {
    this._patchQueriesForm$.next();
  }
  /**
   * Sets up search parameters for current map view (without drawing shapes)
   * Similar to updateShape() but for map bounds
   */
  public setMapViewSearchParams(): void {
    if (!this.map) {
      return;
    }

    const bounds = this.map.getBounds();
    if (!bounds) {
      return;
    }

    // Clear any existing shape state when starting map view search
    if (this._activeShape) { 
      this._activeShape.setMap(null); 
      this._activeShape = null; 
    }
    if (this._activeShapeBbox) { 
      this._activeShapeBbox.setMap(null); 
      this._activeShapeBbox = null; 
    }
    
    // Reset boundary search state since we're clearing the shape
    this.hasBoundarySearched = false;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    // Create bounding box parameters similar to shape drawing
    const fs_bbox = `${sw.lon},${ne.lat},${ne.lon},${sw.lat}`;
    
    
    // Set search parameters similar to updateShape()
    this.queriesSearchParams = {
      fs_bbox: fs_bbox,
      // Add WKB format for axon_connect endpoint
      bboxPolygonWKB: this._createBboxPolygonWKB(ne, sw),
      // Add WKT format for other endpoints  
      bboxPolygon: this._createBboxPolygonWKT(ne, sw)
    };
    
    // Mark that we've searched this map view
    this.hasMapViewSearched = true;
    this.mapInteracted = false; // Disable further searches until interaction
    
    // Emit shape complete to trigger search
    this._shapeComplete$.next(null);
  }

  /**
   * Mark that user has interacted with the map (zoom, drag, draw)
   */
  public markMapInteraction(): void {
    this.mapInteracted = true;
  }

  /**
   * Helper method to create WKB polygon for bounding box
   */
  private _createBboxPolygonWKB(ne: any, sw: any): string {
    // Create WKT first, then convert to WKB
    const wktString = this._createBboxPolygonWKT(ne, sw);
    return this._wktToWkbHex(wktString);
  }

  /**
   * Helper method to create WKT polygon for bounding box
   */
  private _createBboxPolygonWKT(ne: any, sw: any): string {
    // Create WKT polygon for the bounding box
    const nw = { lat: ne.lat, lon: sw.lon };
    const se = { lat: sw.lat, lon: ne.lon };
    
    return `POLYGON((${sw.lon} ${sw.lat}, ${nw.lon} ${nw.lat}, ${ne.lon} ${ne.lat}, ${se.lon} ${se.lat}, ${sw.lon} ${sw.lat}))`;
  }

  private _removeShapeListeners(): void {
    this._allShapeListeners.forEach((listener) => {
      google.maps.event.removeListener(listener);
    });
    if (this._shapeClickListener) {
      google.maps.event.removeListener(this._shapeClickListener);
      this._shapeClickListener = null;
    }
    if (this._shapeMoveListener) {
      google.maps.event.removeListener(this._shapeMoveListener);
      this._shapeMoveListener = null;
    }
    if (this.projectionOverlay) {
      this.projectionOverlay.setMap(null);
    }
  }

  private _getPickInfoFromPoint(latLng: google.maps.LatLng): {
    pickInfo: any;
    containerPoint: google.maps.Point;
  } {
    const projection = this.projectionOverlay.getProjection();
    const containerPoint = projection.fromLatLngToContainerPixel(latLng);
    const pickInfo = this.deckGLoverlay.pickObject({
      x: containerPoint.x,
      y: containerPoint.y,
    });
    return { pickInfo, containerPoint };
  }

  private _trackListener(target: any, event: string, callback: Function): void {
    const listener = google.maps.event.addListener(target, event, callback);
    this._allShapeListeners.push(listener);
  }

  /**
   * Add listeners to the drawn shape for when it is modified.
   *
   * For example, a shape can be dragged to be moved, points of a polygon can be shifted,
   * polygon lines can be bisected, circles and rectangles can be made larger.
   *
   * @param shape a polygon, circle, or rectangle
   */
  private _attachListeners(shape: DrawingShapes): void {
    this.updateShape(shape);
    const bounds = new google.maps.LatLngBounds();
    this._trackListener(shape, 'dragstart', () => {
      this.isDragging = true;
      return;
    });
    this._trackListener(shape, 'dragend', () => {
      this.isDragging = false;
      this.updateShape(shape);
      // Update the shape but don't emit _shapeComplete$ here, as updateShape() will handle that
      return;
    });

    // Set up projection overlay and add click handler for shapes
    this.projectionOverlay.setMap(this.map.mapInstance);
    this._shapeClickListener = google.maps.event.addListener(shape, 'click', (item) => {
      const { pickInfo } = this._getPickInfoFromPoint(item.latLng);
      if (pickInfo) {
        this._markerClicked$.next(pickInfo);
      }
    });

    this._shapeMoveListener = google.maps.event.addListener(shape, 'mousemove', (item) => {
      const { pickInfo, containerPoint } = this._getPickInfoFromPoint(item.latLng);
      this._markerHovered$.next({ pickInfo, containerPoint });
    });

    if (shape instanceof google.maps.Polygon) {
      shape.getPaths().forEach((path) => {
        this._trackListener(path, 'insert_at', () => {
          this.updateShape(shape);
        });

        // This is spam emitted when the polygon is dragged.
        // Debounce to avoid unnecessary boolean checks.
        this._trackListener(
          path,
          'set_at',
          debounce(() => {
            if (this.isDragging) {
              return;
            }
            this.updateShape(shape);
          }, 100),
        );
      });

      const points = shape.getPath();
      this._processPoints(points, bounds.extend, bounds);
    } else if (shape instanceof google.maps.Circle) {
      this._trackListener(shape, 'radius_changed', () => {
        this.updateShape(shape);
      });

      /**
       * Debounce the shape conversion callback to prevent excessive isDragging checking.
       *
       * Moving the circle by dragging the middle dot doesn't trigger the `dragend` event
       * So, we have to use `center_changed`. The downside is moving the circle by dragging
       * anywhere else on the circle spam emits this event.
       * */
      this._trackListener(
        shape,
        'center_changed',
        debounce(() => {
          if (this.isDragging) {
            return;
          }
          this.updateShape(shape);
        }, 100),
      );
      const circlePoints = this.circlePath(shape.getCenter(), shape.getRadius(), 360).map(
        (coor) => new google.maps.LatLng(coor[0], coor[1]),
      );
      const geometry = new google.maps.Data.Polygon([circlePoints]);
      this._processPoints(geometry, bounds.extend, bounds);
    } else if (shape instanceof google.maps.Rectangle) {
      this._trackListener(shape, 'bounds_changed', () => {
        if (this.isDragging) {
          return;
        }
        this.updateShape(shape);
      });
      const rectangleBounds = shape.getBounds();
      bounds.extend(rectangleBounds.getNorthEast());
      bounds.extend(rectangleBounds.getSouthWest());
    }

    this.map.mapInstance.fitBounds(bounds);
  }

  private _processPoints(geometry: any, callback: any, thisArg: any): any {
    if (geometry instanceof google.maps.LatLng) {
      callback.call(thisArg, geometry);
    } else if (geometry instanceof google.maps.Data.Point) {
      callback.call(thisArg, geometry.get());
    } else {
      geometry.getArray().forEach((g) => {
        this._processPoints(g, callback, thisArg);
      });
    }
  }

  /**
   * Calculates the bounding box for a shape in three formats: `fs_bbox`, `bboxPolygon`, and
   * `activeShapeBbox`.
   *
   *  - `fs_bbox` contains the two coordinates to form the opposite corners of the rectangle.
   *  - `bboxPolygon` contains four points for all verticies of the rectangle. Must be converted
   *    into GeoJSON before it can be wkt stringified.
   *  - `activeShapeBbox` the red overlay box that shows the bbox for complex shapes
   *
   * @param shape a polygon, circle, or rectangle
   * @returns
   */
  private _getBboxFromShape(shape: DrawingShapes): {
    fs_bbox: string | null;
    bboxPolygon: google.maps.Data.Feature;
    activeShapeBbox: google.maps.Polygon;
  } {
    let fs_bbox = null;
    let bboxPolygon = null;
    let activeShapeBbox = null;
    let ne = null;
    let sw = null;
    let nw = null;
    let se = null;

    if (shape instanceof google.maps.Polygon) {
      const bounds = new google.maps.LatLngBounds();
      const paths = shape.getPath();
      paths.forEach((path) => {
        bounds.extend(path);
      });
      ne = bounds.getNorthEast();
      sw = bounds.getSouthWest();
      nw = new google.maps.LatLng(sw.lat(), ne.lng());
      se = new google.maps.LatLng(ne.lat(), sw.lng());
    } else if (shape instanceof google.maps.Circle || shape instanceof google.maps.Rectangle) {
      ne = shape.getBounds().getNorthEast();
      sw = shape.getBounds().getSouthWest();
      nw = new google.maps.LatLng(sw.lat(), ne.lng());
      se = new google.maps.LatLng(ne.lat(), sw.lng());
    }

    if (nw && sw && se && ne) {
      fs_bbox = `${sw.lng()},${ne.lat()},${ne.lng()},${sw.lat()}`;
      bboxPolygon = new google.maps.Data.Feature({
        geometry: new google.maps.Data.Polygon([[nw, sw, se, ne]]),
        properties: {
          type: 'polygon',
        },
      });
      activeShapeBbox = new google.maps.Polygon({
        clickable: false,
        editable: false,
        draggable: false,
        paths: [[nw, sw, se, ne]],
        fillColor: this.bBoxFillColor,
        fillOpacity: this.fillOpacity,
        strokeWeight: 0,
        visible: this.showBbox,
      });
    }
    return { fs_bbox, bboxPolygon, activeShapeBbox };
  }

  private _penDrawingMode(): void {
    this._freeDrawPolyline = new google.maps.Polyline({
      map: this.map.mapInstance,
      clickable: false,
      strokeColor: this.activeStrokeColor,
      strokeOpacity: this.strokeOpacity,
      strokeWeight: this.strokeWeight,
    });

    this._penMoveCallListener = google.maps.event.addListener(
      this.map.mapInstance,
      'mousemove',
      (e) => {
        this._freeDrawPolyline.getPath().push(e.latLng);
      },
    );

    google.maps.event.addListenerOnce(this.map.mapInstance, 'mouseup', () => {
      google.maps.event.removeListener(this._penMoveCallListener);
      const isValidPath =
        this._freeDrawPolyline.getPath()?.getArray()?.length &&
        this._freeDrawPolyline.getPath()?.getArray()[0]?.lat() !==
          this._freeDrawPolyline.getPath()?.getArray()[1]?.lat();
      if (!isValidPath) {
        this._drawingMode = null;
        this._freeDrawPolyline.setMap(null);
        google.maps.event.clearListeners(this.map.mapInstance, 'mousedown');
        this.resetMapOptions();
        this._toastrService.error('Invalid boundary');
        return;
      }
      const path = this._freeDrawPolyline.getPath();
      this._freeDrawPolyline.setMap(null);
      const editable = path.length > 20 ? false : true;
      const polygon = new google.maps.Polygon({
        paths: [path],
        editable,
        draggable: true,
        strokeColor: this.activeStrokeColor,
        fillColor: this.activeFillColor,
        fillOpacity: this.fillOpacity,
        strokeOpacity: this.strokeOpacity,
        strokeWeight: this.strokeWeight - 1,
      });
      const dataLayer = new google.maps.Data();
      dataLayer.add(
        new google.maps.Data.Feature({
          geometry: new google.maps.Data.Polygon([
            polygon
              .getPath()
              .getArray()
              .map((latLng) => new google.maps.LatLng(latLng.lat(), latLng.lng())),
          ]),
        }),
      );
      dataLayer.toGeoJson((featureCollection: any) => {
        const simplifiedGeojson = turf.default(featureCollection, {
          tolerance: 0.0001,
          highQuality: true,
          mutate: true,
        });
        const paths = simplifiedGeojson.features[0].geometry.coordinates[0].map((position) => {
          return new google.maps.LatLng(position[1], position[0]);
        });
        const simplifiedPoly = new google.maps.Polygon({
          paths,
          editable: true,
          draggable: true,
          strokeColor: this.activeStrokeColor,
          fillColor: this.activeFillColor,
          fillOpacity: this.fillOpacity,
          strokeOpacity: this.strokeOpacity,
          strokeWeight: this.strokeWeight,
        });
        const polyPaths = simplifiedPoly.getPaths().getArray();
        const polylinePaths = polyPaths.map((p) => p.getArray());
        const hasPaths = [...polylinePaths].length;
        if (hasPaths) {
          simplifiedPoly.setMap(this.map.mapInstance);
          this._shapeComplete$.next(simplifiedPoly);
          this._activeShape = simplifiedPoly;
          this._attachListeners(this._activeShape);
        }
        this._drawingMode = null;
        google.maps.event.clearListeners(this.map.mapInstance, 'mousedown');
        this.resetMapOptions();
      });
    });
  }

  private _initPenMode(): void {
    this._drawingMode = 'pen';
    this.map.mapInstance.setOptions({
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
      draggableCursor: 'crosshair',
    });
    google.maps.event.addListener(this.map.mapInstance, 'mousedown', () => {
      this._penDrawingMode();
    });
  }

  private _pointInCircle(
    point: google.maps.LatLng,
    radius: number,
    center: google.maps.LatLng,
  ): boolean {
    return google.maps.geometry.spherical.computeDistanceBetween(point, center) <= radius;
  }

  private _resetParams(): void {
    delete this.queriesSearchParams.bboxPolygon;
    this.queriesSearchParams = {};
  }

  private _clearUrlQueryParams(): void {
    this.showBbox = false;
    this.toggleShapes(true);
    this._resetParams();

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {},
    });
  }

  private _initProjectionOverlay(): void {
    this.projectionOverlay = new google.maps.OverlayView();
    this.projectionOverlay.onAdd = () => {};
    this.projectionOverlay.draw = () => {};
    this.projectionOverlay.onRemove = () => {};
  }

  /**
   * Converts WKT (Well-Known Text) to WKB (Well-Known Binary) hex string
   * This avoids WAF security triggers that mistake WKT for SQL injection
   *
   * @param wktString The WKT string to convert to WKB
   * @returns Hex string representation of WKB that's safe for transmission
   */
  private _wktToWkbHex(wktString: string): string {
    try {
      // Parse WKT string to geometry
      const geometry = wkx.Geometry.parse(wktString);
      // Convert to WKB hex string
      return geometry.toWkb().toString('hex');
    } catch (error) {
      console.error('Error converting WKT to WKB:', error);
      // Return empty polygon WKB if conversion fails
      return EMPTY_POLYGON_WKB;
    }
  }
}
